import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { vocabularyApi } from '../../api/global';
import CheckBox from '../common/CheckBox';
import { ORDER_TYPE } from '../../data/common';
import FoldingGrayIc from '../../assets/images/ic_folding_arrow.svg';
import FoldingBlueIc from '../../assets/images/ic_folding_arrow_blue.svg';
import Loading from '../common/Loading';

function UnitChoice({
  manualIdx = undefined,
  unitList,
  checkedUnitIdxList,
  setCheckedUnitIdxList,
  setVocabularyList,
  isFolding,
  setIsFolding,
  setPrintSetOpen,
  savedPaper = null,
  setGeneratedPaper,
  checkedStatistics,
  setCheckedStatistics,
  orderingCheck,
  isExchange = undefined,
}) {
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // 저장한 시험지에 대한 어휘들 불러오기
  useEffect(() => {
    (async () => {
      if (savedPaper !== null && unitList?.length >= 1) {
        try {
          setLoading(true);
          setIsFolding(prev => ({ ...prev, unit: true, type: true }));
          setPrintSetOpen({ set: true, print: true });
          setCheckedUnitIdxList(savedPaper.unit_ids);
          const apiParams = {
            bookId: manualIdx,
            unitIds: savedPaper.unit_ids,
          };
          const { data } = await vocabularyApi.getVocas(apiParams);
          const vocas = data.vocas
            .sort((a, b) => {
              if (a.un_idx === b.un_idx) {
                return a.vc_order - b.vc_order;
              }
              return a.un_idx - b.un_idx;
            })
            .map(voca => {
              const foundType = savedPaper.questions.find(typeQuestions =>
                typeQuestions.questions.some(question => {
                  const equalYn =
                    question.word === voca.word && question.wordId === voca.idx
                      ? true
                      : false;
                  return equalYn;
                }),
              );
              return {
                ...voca,
                chosenType: foundType ? foundType.type : null,
                checked: !!foundType,
                shouldShow: foundType !== undefined ? true : false,
              };
            });
          setVocabularyList(vocas);
          setGeneratedPaper(savedPaper.questions);
          // 시험지중 있는 표/파/참/관 반영
          const extractVcTypes = vocas => {
            const uniqueTypes = new Set();
            const targetTypes = new Set([1, 2, 3, 4]);
            for (const voca of vocas) {
              if (voca.shouldShow && targetTypes.has(voca.vc_type)) {
                uniqueTypes.add(voca.vc_type);
              }
              if (uniqueTypes.size === targetTypes.size) {
                break;
              }
            }
            return uniqueTypes.size > 0 ? [...uniqueTypes] : null;
          };
          setCheckedStatistics(extractVcTypes(vocas));
          setLoading(false);
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedPaper, unitList, manualIdx]);

  // 유닛 전체선택시 단어들 호출
  const handleAllCheck = async checked => {
    if (checked) {
      try {
        setLoading(true);
        setCheckedUnitIdxList(unitList.map(unit => unit.idx));
        const unitIdxList = unitList.map(unit => unit.idx);
        const apiParams = {
          bookId: manualIdx,
          unitIds: unitIdxList.filter(
            idx => !checkedUnitIdxList?.includes(idx),
          ),
        };
        const { data } = await vocabularyApi.getVocas(apiParams);
        const vocas = data.vocas.map(voca => ({
          ...voca,
          chosenType: null,
          checked: false,
          shouldShow: checkedStatistics.includes(voca.vc_type) ? true : false,
        }));
        setVocabularyList(prev => {
          const combinedVocas = [...prev, ...vocas];
          if (orderingCheck === ORDER_TYPE.BOOK) {
            return combinedVocas.sort((a, b) => {
              if (a.un_idx === b.un_idx) {
                return a.vc_order - b.vc_order;
              }
              return a.un_idx - b.un_idx;
            });
          } else if (orderingCheck === ORDER_TYPE.ALPHABET) {
            return combinedVocas.sort((a, b) =>
              a.word.localeCompare(b.word, 'en', { ignorePunctuation: true }),
            );
          } else {
            return combinedVocas;
          }
        });
        setLoading(false);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    } else {
      setCheckedUnitIdxList([]);
      setVocabularyList([]);
    }
  };

  // 유닛 개별 선택시 단어들 호출
  const handleSingleCheck = async (checked, idx) => {
    if (checked) {
      try {
        setLoading(true);
        setCheckedUnitIdxList([...checkedUnitIdxList, idx]);
        const apiParams = {
          bookId: manualIdx,
          unitIds: [idx],
        };
        const { data } = await vocabularyApi.getVocas(apiParams);
        const vocas = data.vocas.map(voca => ({
          ...voca,
          chosenType: null,
          checked: false,
          shouldShow: checkedStatistics.includes(voca.vc_type) ? true : false,
        }));
        setVocabularyList(prev => {
          const combinedVocas = [...prev, ...vocas];
          if (orderingCheck === ORDER_TYPE.BOOK) {
            return combinedVocas.sort((a, b) => {
              if (a.un_idx === b.un_idx) {
                return a.vc_order - b.vc_order;
              }
              return a.un_idx - b.un_idx;
            });
          } else if (orderingCheck === ORDER_TYPE.ALPHABET) {
            return combinedVocas.sort((a, b) =>
              a.word.localeCompare(b.word, 'en', { ignorePunctuation: true }),
            );
          } else {
            return combinedVocas;
          }
        });
        setLoading(false);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    } else {
      setCheckedUnitIdxList(checkedUnitIdxList.filter(item => item !== idx));
      setVocabularyList(prev => {
        const filteredVocas = prev.filter(item => item.un_idx !== idx);
        if (orderingCheck === ORDER_TYPE.BOOK) {
          return filteredVocas.sort((a, b) => {
            if (a.un_idx === b.un_idx) {
              return a.vc_order - b.vc_order;
            }
            return a.un_idx - b.un_idx;
          });
        } else if (orderingCheck === ORDER_TYPE.ALPHABET) {
          return filteredVocas.sort((a, b) =>
            a.word.localeCompare(b.word, 'en', { ignorePunctuation: true }),
          );
        }
        return filteredVocas;
      });
    }
  };

  useEffect(() => {
    if (isExchange && !loading && unitList?.length >= 1) {
      scrollRef.current.scrollTo(0, 0);
    }
  }, [isExchange, loading, unitList]);

  return (
    <div
      className="flex flex-col justify-start items-center bg-[#FFF] h-full min-w-[163px] border-r-[3px] border-[#969ba4] transition-all overflow-hidden"
      style={{
        minWidth: isFolding ? '41px' : '163px',
        width: isFolding ? '41px' : '163px',
      }}
    >
      {loading && <Loading />}
      <div
        className="w-full flex  min-h-[54px] h-[54px] px-[8px] py-[10px] items-center justify-between border-b border-[#E7E7E7]"
        style={isFolding ? { border: 'none' } : {}}
      >
        <div
          className="flex justify-between items-center gap-1"
          style={{ display: isFolding ? 'none' : 'flex' }}
        >
          <CheckBox
            value="all"
            isChecked={
              checkedUnitIdxList?.length === unitList?.length ? true : false
            }
            onChange={handleAllCheck}
            boxStyle={{ padding: 0 }}
          />
          <div className="text-[14px] ml-[-7px] flex items-center overflow-hidden whitespace-nowrap">
            선택 유닛 수
          </div>
          <div className="font-bold flex items-center">
            {checkedUnitIdxList?.length}
          </div>
        </div>
        <button
          type="button"
          onClick={() =>
            setIsFolding(prev => {
              return { ...prev, unit: !prev.unit };
            })
          }
        >
          <img
            src={isFolding ? FoldingBlueIc : FoldingGrayIc}
            alt="folding"
            style={{
              minWidth: '22px',
              minHeight: '22px',
            }}
          />
        </button>
      </div>
      <div
        className="flex justify-center text-[14px] font-[700] mt-[-5px]"
        style={{ display: isFolding ? 'flex' : 'none' }}
      >
        유닛
      </div>
      <div
        ref={scrollRef}
        className="justify-start items-start overflow-y-auto"
        style={{ display: isFolding ? 'none' : 'grid' }}
      >
        {unitList?.length >= 1 &&
          unitList
            ?.sort((a, b) => a.un_order - b.un_order)
            ?.map(unit => {
              const isChecked = checkedUnitIdxList?.includes(unit.idx);
              return (
                <div
                  key={unit.idx}
                  className="border-b border-[#E7E7E7] flex items-center"
                  style={{ background: isChecked ? '#F2EDF9' : '#FFF' }}
                >
                  <CheckBox
                    id={unit.idx}
                    value={unit.idx}
                    isChecked={isChecked}
                    onChange={handleSingleCheck}
                    label={unit.un_short}
                    labelStyle={
                      isChecked
                        ? {
                            fontWeight: 700,
                            color: '#773DBD',
                            width: '125px',
                          }
                        : {
                            fontWeight: 400,
                            color: '#94A3B8',
                            width: '125px',
                          }
                    }
                    boxStyle={{ padding: '9px 8px' }}
                  />
                </div>
              );
            })}
      </div>
    </div>
  );
}

UnitChoice.propTypes = {
  manualIdx: PropTypes.number,
  unitList: PropTypes.array.isRequired,
  checkedUnitIdxList: PropTypes.array.isRequired,
  setCheckedUnitIdxList: PropTypes.func.isRequired,
  setVocabularyList: PropTypes.func.isRequired,
  isFolding: PropTypes.bool.isRequired,
  setIsFolding: PropTypes.func.isRequired,
  setPrintSetOpen: PropTypes.func.isRequired,
  savedPaper: PropTypes.object,
  setGeneratedPaper: PropTypes.func.isRequired,
  checkedStatistics: PropTypes.array.isRequired,
  setCheckedStatistics: PropTypes.func.isRequired,
  orderingCheck: PropTypes.number.isRequired,
  isExchange: PropTypes.bool,
};

export default React.memo(UnitChoice);
